import React from 'react';
import {
  DefaultConf,
  useDataTable as useIamDataTable,
  useDataTableBasic as useIamDataTableBasic
} from '@iamsoftware/react-hooks';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';

type AppDataTableOptions = {
  recordLabel?: string | ((totalRecords: number) => React.ReactNode);
  headerActions?: React.ReactNode;
};

const isIndexColumn = (node: React.ReactNode, indexColumnWidth?: number): node is React.ReactElement<any> => {
  if (!React.isValidElement<any>(node)) return false;
  const props = node.props;
  return Boolean(
    indexColumnWidth
    && node.type === Column
    && props.header === DefaultConf.DataTable.IndexColumnHeader
    && props.headerStyle?.width === indexColumnWidth
    && String(props.className || '').includes('white-space-nowrap')
  );
};

const isFlexibleDataColumnCandidate = (node: React.ReactNode): node is React.ReactElement<any> => {
  if (!React.isValidElement<any>(node) || node.type !== Column) return false;
  const props = node.props;
  return !props.selectionMode
    && props.header !== undefined
    && !String(props.className || '').includes('table-actions');
};

const patchTable = (
  node: React.ReactNode,
  indexColumnWidth?: number,
  options: AppDataTableOptions = {}
): React.ReactNode => {
  if (!React.isValidElement<any>(node)) return node;

  const props = node.props;
  let children: React.ReactNode[] | undefined;
  if (props.children !== undefined) {
    children = React.Children.toArray(props.children)
      .map(child => patchTable(child, indexColumnWidth, options));

    const indexPosition = children.findIndex(child => isIndexColumn(child, indexColumnWidth));
    if (indexPosition >= 0) {
      const flexibleColumnPosition = children.findIndex(
        (child, position) => position > indexPosition && isFlexibleDataColumnCandidate(child)
      );
      if (flexibleColumnPosition >= 0) {
        const flexibleColumn = children[flexibleColumnPosition] as React.ReactElement<any>;
        const {
          width: _width,
          maxWidth: _maxWidth,
          ...flexibleStyle
        } = flexibleColumn.props.style || {};
        children[flexibleColumnPosition] = React.cloneElement(flexibleColumn, {
          style: flexibleStyle
        });
      }
    }
  }

  const indexColumn = isIndexColumn(node, indexColumnWidth);
  const dataTable = node.type === DataTable;
  const totalRecords = Number(props.totalRecords ?? props.value?.length ?? 0);
  const recordSummary = typeof options.recordLabel === 'function'
    ? options.recordLabel(totalRecords)
    : `${totalRecords} ${options.recordLabel}`;

  const patchedProps = indexColumn
    ? {
        style: {
          ...props.style,
          width: indexColumnWidth,
          minWidth: indexColumnWidth,
          maxWidth: indexColumnWidth
        },
        headerStyle: {
          ...props.headerStyle,
          width: indexColumnWidth,
          minWidth: indexColumnWidth,
          maxWidth: indexColumnWidth
        },
        bodyStyle: {
          ...props.bodyStyle,
          width: indexColumnWidth,
          minWidth: indexColumnWidth,
          maxWidth: indexColumnWidth
        }
      }
    : dataTable && options.recordLabel
      ? {
          header: (
            <div className="flex align-items-center justify-content-between flex-wrap gap-2 w-full">
              <span>{recordSummary}</span>
              {options.headerActions && (
                <div className="flex align-items-center flex-wrap gap-2">
                  {options.headerActions}
                </div>
              )}
            </div>
          )
        }
      : {};

  return children === undefined
    ? React.cloneElement(node, patchedProps)
    : React.cloneElement(node, patchedProps, children);
};

export const useDataTable = (
  props: Parameters<typeof useIamDataTable>[0] & AppDataTableOptions
) => {
  const { recordLabel, headerActions, ...iamProps } = props;
  const result = useIamDataTable(iamProps);
  return {
    ...result,
    render: () => patchTable(
      result.render(),
      props.indexColumnWidth,
      { recordLabel, headerActions }
    ) as React.ReactElement
  };
};

export const useDataTableBasic = (
  props: Parameters<typeof useIamDataTableBasic>[0] & AppDataTableOptions
) => {
  const { recordLabel, headerActions, ...iamProps } = props;
  const result = useIamDataTableBasic(iamProps);
  return {
    ...result,
    render: () => patchTable(
      result.render(),
      props.indexColumnWidth,
      { recordLabel, headerActions }
    ) as React.ReactElement
  };
};
